// React Imports
import { forwardRef, useEffect, useState } from 'react';
import type { ForwardRefRenderFunction, HTMLAttributes, MutableRefObject } from 'react';

// Third-party Imports
import PerfectScrollbar from 'react-perfect-scrollbar';

import type { ChildrenType, RootStylesType } from '../../types';
// Type Imports
import type { VerticalMenuContextProps } from './Menu';

// Styled Component Imports
import StyledSubMenuContent from '../../styles/StyledSubMenuContent';

// Style Imports
import styles from '../../styles/styles.module.css';

export type SubMenuContentProps = HTMLAttributes<HTMLDivElement> &
  RootStylesType &
  Partial<ChildrenType> & {
    open?: boolean;
    openWhenCollapsed?: boolean;
    openWhenHovered?: boolean;
    transitionDuration?: VerticalMenuContextProps['transitionDuration'];
    isPopoutWhenCollapsed?: boolean;
    level?: number;
    isCollapsed?: boolean;
    isHovered?: boolean;
    browserScroll?: boolean;
  };

const SubMenuContent: ForwardRefRenderFunction<HTMLDivElement, SubMenuContentProps> = (props, ref) => {
  // Props
  const {
    children,
    open,
    level,
    isCollapsed,
    isHovered,
    transitionDuration,
    isPopoutWhenCollapsed,
    openWhenCollapsed,
    browserScroll,
    ...rest
  } = props;

  // States
  const [mounted, setMounted] = useState(false);

  // Refs
  const SubMenuContentRef = ref as MutableRefObject<HTMLDivElement>;

  useEffect(() => {
    if (mounted) {
      if (open || (open && isHovered)) {
        const target = SubMenuContentRef?.current;

        if (target) {
          target.style.maxHeight = `${target.scrollHeight}px`;
        }
      } else {
        const target = SubMenuContentRef?.current;

        if (target) {
          target.style.maxHeight = '';
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mounted, SubMenuContentRef]);

  useEffect(() => {
    setMounted(true);
  }, [isHovered]);

  return (
    <StyledSubMenuContent
      ref={ref}
      level={level}
      isCollapsed={isCollapsed}
      isHovered={isHovered}
      open={open}
      openWhenCollapsed={openWhenCollapsed}
      isPopoutWhenCollapsed={isPopoutWhenCollapsed}
      transitionDuration={transitionDuration}
      browserScroll={browserScroll}
      {...rest}
    >
      {/* If browserScroll is false render PerfectScrollbar */}
      {!browserScroll && level === 0 && isPopoutWhenCollapsed && isCollapsed ? (
        <PerfectScrollbar
          options={{ wheelPropagation: false, suppressScrollX: true }}
          style={{ maxBlockSize: `calc((var(--vh, 1vh) * 100))` }}
        >
          <ul className={styles.ul}>{children}</ul>
        </PerfectScrollbar>
      ) : (
        <ul className={styles.ul}>{children}</ul>
      )}
    </StyledSubMenuContent>
  );
};

export default forwardRef(SubMenuContent);
